/**
 * Wrapper component for AddUserRoleDialog to work with university admin management
 * This bridges the interface gap between the generic dialog and the data management config
 */

import React from 'react';
import { AddUserRoleDialog, AddUserRoleDialogConfig } from '@shared/components/user-role';
import { useInviteUniversityAdmin } from '../hooks/useUniversityAdmins';
import { useUserExists } from '@shared/hooks/useUserExists';
interface AddUniversityAdminDialogProps {
  open: boolean;
  onClose: () => void;
  editingItem?: any; // Not used for invite flow
  onSuccess?: () => void;
  tenantId: string;
}

export const AddUniversityAdminDialog: React.FC<AddUniversityAdminDialogProps> = ({
  open,
  onClose,
  onSuccess,
  tenantId,
}) => {
  const inviteMutation = useInviteUniversityAdmin(tenantId);

  const config: AddUserRoleDialogConfig = {
    title: 'Add University Administrator',
    description: 'Invite a user to become an administrator for this university.',
    submitLabel: 'Send Invitation',
    emailLabel: 'Email Address',
    nameLabel: 'Full Name',
    nameRequired: false,
    onSubmit: async (data) => {
      await inviteMutation.mutateAsync({
        email: data.email,
        name: data.name || undefined,
        tenantId: tenantId,
      });
    },
    useUserExistsHook: (email, enabled) => {
      const result = useUserExists(email, { enabled });
      return {
        data: result.data
          ? {
              exists: result.exists ?? false,
              user: result.exists
                ? {
                    name: result.data.user?.name,
                    email: result.data.user?.email || result.data.email,
                  }
                : undefined,
            }
          : undefined,
        isLoading: result.isLoading,
      };
    },
  };

  return (
    <AddUserRoleDialog
      open={open}
      onClose={onClose}
      onSuccess={onSuccess}
      config={config}
      isLoading={inviteMutation.isPending}
    />
  );
};
